import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { canAccessSurveyResponses } from '../../constants/surveyAccessPermissions';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';

/**
 * Tenant-wide responses index — pick a survey to open its response detail page.
 */
export default function SurveyResponsesHubPage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const { hasPermission } = useTenant();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const canView = canAccessSurveyResponses(hasPermission);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch('/admin/responses/summary');
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setErr(getApiErrorMessage(e, 'Failed to load responses'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, canView]);

  if (!canView) {
    return (
      <div className="page">
        <div className="alert error">You do not have permission to view responses.</div>
        <Link className="btn secondary" to="/survey/manage">
          Back to surveys
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading responses…
        </Typography>
      </Box>
    );
  }

  if (err) {
    return (
      <div className="page">
        <div className="alert error">{err}</div>
        <Link className="btn secondary" to="/survey/manage">
          Back to surveys
        </Link>
      </div>
    );
  }

  const list = rows ?? [];

  return (
    <div className="page survey-responses-hub-page">
      <header className="page-header">
        <h1>View responses</h1>
        <p className="lede">Select a survey to review submitted responses.</p>
        <p className="lede" style={{ marginTop: '-1rem', marginBottom: '1.25rem' }}>
          <Link to="/survey/manage">← Manage surveys</Link>
        </p>
      </header>

      {list.length === 0 ? (
        <div className="card survey-responses-hub-empty">
          <p className="muted" style={{ margin: 0 }}>
            No surveys have responses yet. Publish a survey and collect submissions to see them here.
          </p>
        </div>
      ) : (
        <div className="grid two survey-responses-hub-grid">
          {list.map((row) => (
            <article key={row.survey_id} className="card interactive survey-responses-hub-card">
              <div className="card-head">
                <h2>{row.survey_title}</h2>
              </div>
              <ul className="tag-list survey-responses-hub-tags">
                <li>
                  <span className="tag">
                    {row.total_responses} submission{row.total_responses === 1 ? '' : 's'}
                  </span>
                </li>
              </ul>
              <p className="muted small survey-responses-hub-last">
                Last submitted{' '}
                {row.last_submitted_at
                  ? new Date(row.last_submitted_at).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })
                  : '—'}
              </p>
              <div className="survey-responses-hub-foot">
                <Link className="btn primary survey-responses-hub-btn" to={`/survey/${row.survey_id}/responses`}>
                  View responses
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
